import { getFromColorMap, isGreyscale, isThemeColor, isColor } from '../color'
import { SvgShadow, SvgLayout, SvgInfo, SvgProperties, MaskMode, MaskFlip } from './typing'
import { getShadow, getFilter } from './svgFilters'
import { parseSvgString } from './svgParser'
import Color from 'color'
import { ColorMap } from '@wix/thunderbolt-becky-types'
import { pickBy } from 'lodash'
import { compIdToCssSelector } from '../hoverBoxUtils'

type Flip = 'x' | 'y' | 'xy' | 'none'

export type MaskData = {
	svgString: string
	flip: Flip
	rotate: number
}

export type ImageDimensions = {
	width: number
	height: number
}

type MaskDataAttrs = {
	width: string
	height: string
	x: number
	y: number
	transform: string | null
	style?: string
}

const viewBoxMatcher = /viewBox="([^"]*)"/i
const svgWidthMatcher = /(?:<svg[^>]*)\s(width="[^"]*")/i
const svgHeightMatcher = /(?:<svg[^>]*)\s(height="[^"]*")/i
const svgTagMatcher = /(<svg[^>]*)(>)/
const fillAttributeMatcher = /fill="(.*?)"/gi
const styleAttributeMatcher = /style="([^"]*)"/i
const styleTransformMatcher = /transform:([^;]*)/i

const SVG_TYPES = {
	SHAPE: 'shape',
	TINT: 'tint',
	COLOR: 'color',
	UGC: 'ugc',
}

function replaceSVGMaskAttributes(svgString: string, svgId: string) {
	if (!svgString) {
		return svgString
	}
	const svgTagMatch = svgString.match(/<svg[^>]*>/)

	if (svgTagMatch) {
		let svgTag = svgTagMatch[0]
		const idMatch = svgTag.match(/id="[-\w]+"/)
		const aspectRatioMatch = svgTag.match(/preserveAspectRatio="[-\w\s]+"/)

		if (aspectRatioMatch) {
			svgTag = svgTag.replace(aspectRatioMatch[0], 'preserveAspectRatio="none"')
		} else {
			svgTag = svgTag.replace('<svg ', '<svg preserveAspectRatio="none" ')
		}

		if (idMatch) {
			svgTag = svgTag.replace(idMatch[0], `id="${svgId}"`)
		} else {
			svgTag = svgTag.replace('<svg ', `<svg id="${svgId}" `)
		}

		svgString = svgString.replace(svgTagMatch[0], svgTag)
	}

	return svgString
}

const symbols = /[\r\n%#()<>?[\\\]^`{|}]/g

/**
 * Returns a data URI SVG encoded for CSS <url> value
 */
function encodeSVG(svg: string) {
	const escaped = svg
		.replace(/"/g, "'") // Use single quotes instead of double to avoid encoding.
		.replace(/>\s+</g, '><') // trim
		.replace(/\s{2,}/g, ' ') // trim
		.replace(symbols, encodeURIComponent) // encode symbols

	return `url("data:image/svg+xml,${escaped}")`
}

function transformToTintColors(svgString: string, resolvedColor: string) {
	const baseColor = Color(resolvedColor)
	return svgString.replace(fillAttributeMatcher, (__, colorToTint) => {
		if (colorToTint === 'none') {
			return 'fill="none"'
		}
		const colorObj = Color(colorToTint)
		if (isGreyscale(colorObj)) {
			const tint = 1 - (255 - colorObj.red()) / 255 // eslint-disable-line no-mixed-operators
			const rTint = Math.floor(baseColor.red() + (255 - baseColor.red()) * tint) // eslint-disable-line no-mixed-operators
			const gTint = Math.floor(baseColor.green() + (255 - baseColor.green()) * tint) // eslint-disable-line no-mixed-operators
			const bTint = Math.floor(baseColor.blue() + (255 - baseColor.blue()) * tint) // eslint-disable-line no-mixed-operators
			const tintedColor = Color({ r: rTint, g: gTint, b: bTint })
			// return tinted color
			return `fill="${tintedColor.hex().toString()}"`
		}
		// no change, return original svg color
		return `fill="${colorToTint}"`
	})
}

function removeWidthAndHeight(svgString: string) {
	if (svgString) {
		const widthMatch = svgString.match(svgWidthMatcher)
		const heightMatch = svgString.match(svgHeightMatcher)
		if (widthMatch && widthMatch.length > 1) {
			svgString = svgString.replace(widthMatch[1], 'width="100%"')
		}
		if (heightMatch && heightMatch.length > 1) {
			svgString = svgString.replace(heightMatch[1], 'height="100%"')
		}
	}
	return svgString
}

function addA11yFeatures(svgString: string, compId: string, altText?: string) {
	const newSvgString = svgString.replace(svgTagMatcher, '$1 role="img"$2')
	if (typeof altText === 'string') {
		const svgLabelId = `${compId}-svgtitle`
		return newSvgString.replace(
			svgTagMatcher,
			`$1 aria-labelledby="${svgLabelId}"$2<title id="${svgLabelId}">${altText}</title>`
		)
	}
	return newSvgString
}

function getFilterRectInPixels(layout: SvgLayout | null, shadow: SvgShadow) {
	const { blurRadius, x, y } = shadow
	// STD to pixel ~ STD value * 3 , we are multiplying by 6 for both sides
	const blurSpread = blurRadius! * 6
	const filterAttr = {
		x: Math.min(0, x!) - blurSpread / 2,
		y: Math.min(0, y!) - blurSpread / 2,
	}
	let res = `x="${filterAttr.x}" y="${filterAttr.y}"`
	if (layout != null) {
		// In bolt the width and height values are only relevant in the classic editor. In EditorX they end up as NaN
		// In order to have parity with the Bolt implementation, we leave width and height undefined,
		// We avoid NaN values because it results in console errors
		const width = layout.width + blurSpread + Math.abs(x!)
		const height = layout.height + blurSpread + Math.abs(y!)
		res += ` width="${width}" height="${height}"`
	}
	return res
}

function getShadowFilter(
	filterId: string,
	shadow: SvgShadow,
	layout: SvgLayout | null,
	hexColor: string,
	mergeGraphic: boolean
) {
	const filterString = getShadow(filterId, { ...shadow, color: hexColor, mergeGraphic })
	return filterString.replace(
		/<filter /,
		`<filter ${getFilterRectInPixels(layout, shadow)} filterUnits="userSpaceOnUse" `
	)
}

function getPreserveAspectRatioString(displayMode: string | undefined) {
	const preserveAspectRatio = displayMode !== 'stretch'
	const preserveAspectRatioString = preserveAspectRatio ? 'xMidYMid meet' : 'none'
	return { preserveAspectRatio, preserveAspectRatioString }
}

function getScaledSvgViewBox(svgString: string, svgInfo: Partial<SvgInfo>, properties: SvgProperties = {}) {
	if (svgString) {
		const { svgType, viewBox, bbox } = svgInfo
		const { preserveViewBox, displayMode } = properties

		const { preserveAspectRatioString } = getPreserveAspectRatioString(displayMode)

		svgString = svgString.replace(/<svg/, `<svg preserveAspectRatio="${preserveAspectRatioString}"`)

		if (!svgString.match(viewBoxMatcher)) {
			if (svgType === SVG_TYPES.UGC && viewBox) {
				svgString = svgString.replace(/<svg/, `<svg viewBox="${viewBox}"`)
			}
			if (svgType !== SVG_TYPES.UGC && bbox) {
				svgString = svgString.replace(/<svg/, `<svg viewBox="${bbox}"`)
			}
		}

		if (svgType !== SVG_TYPES.UGC && !preserveViewBox && bbox) {
			svgString = svgString.replace(viewBoxMatcher, `viewBox="${bbox}"`)
		}
	}

	return svgString
}

function wrapWithShadow({
	compId,
	svgString,
	layout,
	colorsMap,
	shadow,
	shadowOnly = false,
}: {
	compId: string
	svgString: string
	layout: SvgLayout | null
	colorsMap?: ColorMap
	shadow?: SvgShadow
	shadowOnly?: boolean
}) {
	if (shadow && shadow.color) {
		const filterId = `${compId}-shadow`
		const hexColor = getFromColorMap(shadow.color, colorsMap)
		const shadowFilter = getShadowFilter(filterId, shadow, layout, hexColor, !shadowOnly)
		const modifiedContent = removeWidthAndHeight(svgString)
		return `
          <svg height="100%" width="100%">
              <defs>${shadowFilter}</defs>
              <g filter="url(#${filterId})">
                  ${modifiedContent}
              </g>
          </svg>
      `
	}
	return svgString
}

function getCSSDropShadow(shadow?: SvgShadow, colorsMap?: ColorMap): string {
	if (shadow && shadow.color && shadow.opacity) {
		const hexColor = getFromColorMap(shadow.color, colorsMap)
		const opacity = Math.round(shadow.opacity * 255).toString(16)
		return `drop-shadow(${shadow.x}px ${shadow.y}px ${shadow.blurRadius}px ${hexColor}${opacity.padStart(2, '0')})`
	}

	return ''
}

function getCSSStrokeAdjustments(strokeWidth: number) {
	if (strokeWidth) {
		return {
			svgCalculatedPadding: `${Math.floor(strokeWidth / 2)}px ${Math.ceil(strokeWidth / 2)}px ${Math.ceil(
				strokeWidth / 2
			)}px ${Math.floor(strokeWidth / 2)}px`,
			svgCalculatedWidth: `calc(100% - ${strokeWidth}px)`,
			svgCalculatedHeight: `calc(100% - ${strokeWidth}px)`,
		}
	}
	return {}
}

const FLIP_DATA_TO_TRANSFORM = {
	x: 'scale(-1, 1)',
	y: 'scale(1, -1)',
	xy: 'scale(-1, -1)',
	none: '',
}

function getFlippedSVG(svgString: string, flip: keyof typeof FLIP_DATA_TO_TRANSFORM) {
	const topSvg = svgString.match(svgTagMatcher)
	const transform = FLIP_DATA_TO_TRANSFORM[flip] || ''

	if (topSvg && transform) {
		const styleMatch = topSvg[0].match(styleAttributeMatcher)
		let replacement = `$1 style="transform: ${transform};"$2`

		if (styleMatch) {
			const transformMatch = styleMatch[1].match(styleTransformMatcher)

			if (transformMatch) {
				replacement = topSvg[0].replace(
					styleMatch[0],
					styleMatch[0].replace(transformMatch[0], `transform: ${transform} ${transformMatch[1]}`)
				)
			} else {
				replacement = topSvg[0].replace(styleMatch[0], `style="transform: ${transform}; ${styleMatch[1]}"`)
			}
		}

		return svgString.replace(svgTagMatcher, replacement)
	}

	return svgString
}

function getSvgContentForMask(
	svgString: string,
	svgInfo: SvgInfo,
	flip: MaskFlip,
	mode: MaskMode,
	colorsMap: Array<string>,
	compId: string,
	layout: SvgLayout | null,
	shadow: SvgShadow | null
) {
	const content = getScaledSvgViewBox(svgString, svgInfo, { displayMode: 'stretch' })

	// we must have the transform style inlined inside the SVG content
	const svg = getFlippedSVG(content, flip)

	if (mode === 'drop-shadow' && shadow && layout) {
		return wrapWithShadow({ compId, svgString: svg, layout, colorsMap, shadow, shadowOnly: true })
	}

	return svg
}

function addSvgStyleNode(svgString: string, defsValue: string) {
	if (!svgString) {
		return svgString
	}
	return svgString.replace(/(<svg[^>]*>)/, `$1<defs><style>${defsValue}</style></defs>`)
}

const DEFAULT_COLOR = '#242323'

export type VectorImageTransformationOptions = {
	altText?: string
	compId: string
	strokeWidth: number
	svgInfo: SvgInfo
	properties?: SvgProperties
	layout: SvgLayout
	shadow?: SvgShadow
	colorsMap?: Array<string>
	overrideColors?: Record<string, string>
	svgId: string
	isResponsive?: boolean
}

const pickValidColors = (obj: Record<string, string>) =>
	Object.keys(obj).reduce((result: Record<string, string>, key) => {
		const colorValue = obj[key]
		if (isColor(colorValue) || isThemeColor(colorValue)) {
			result[key] = colorValue
		}
		return result
	}, {})

const transformVectorImage = (
	rawSvg: string,
	{
		altText,
		compId,
		svgInfo,
		properties,
		layout,
		shadow,
		isResponsive,
		colorsMap,
		overrideColors: overrideColorsInuput,
	}: VectorImageTransformationOptions
) => {
	const overrideColors = pickValidColors(overrideColorsInuput || {})

	function withA11y(svgString: string) {
		return addA11yFeatures(svgString, compId, altText)
	}

	function withScaledViewBox(svgString: string) {
		return getScaledSvgViewBox(svgString, svgInfo, properties)
	}

	function withShadowFilter(svgString: string) {
		return wrapWithShadow({
			compId,
			svgString,
			layout: isResponsive ? null : layout,
			colorsMap,
			shadow,
		})
	}

	function withTintColors(svgString: string) {
		const rawColor = (overrideColors && overrideColors.color1) || DEFAULT_COLOR
		const color = getFromColorMap(rawColor, colorsMap)
		return svgInfo.svgType === SVG_TYPES.TINT ? transformToTintColors(svgString, color) : svgString
	}

	function withCssOverrides(svgString: string) {
		const shouldOverrideColors = svgInfo.svgType === SVG_TYPES.COLOR && Object.keys(overrideColors).length > 0
		if (shouldOverrideColors) {
			const colors = Object.entries<string>(overrideColors)
				.map(([colorName, rawColor]) => {
					const resolvedColor = getFromColorMap(rawColor, colorsMap)
					const index = colorName.replace('color', '')
					return `${compIdToCssSelector(compId, true)} svg [data-color="${index}"] {fill: ${resolvedColor};}`
				})
				.join('\n')
			return addSvgStyleNode(svgString, colors)
		}
		return svgString
	}

	const content = withScaledViewBox(withTintColors(withA11y(rawSvg)))

	return withCssOverrides(isResponsive ? content : withShadowFilter(content))
}

const getSvgAttributes = (componentAttributes: Record<string, string>, compId: string) => {
	return {
		ref: `svg${compId}`,
		key: `svg${compId}`,
		id: `svg${compId}`,
		version: '1.1',
		...componentAttributes,
	}
}

const getMaskFlipTransformAttr = (flip: MaskData['flip'], imageDimensions: ImageDimensions) => {
	const width = imageDimensions.width
	const height = imageDimensions.height
	switch (flip) {
		case 'x':
			return { transform: `translate(0 ${height}) scale(1 -1)` }
		case 'y':
			return { transform: `translate(${width} 0) scale(-1 1)` }
		case 'xy':
			return { transform: `translate(${width} ${height}) scale(-1 -1)` }
		default:
			return { transform: null }
	}
}

const getSvgImageAttributes = (filterId: string, maskId: string, displayMode?: string) => {
	const preserveAspectRatio =
		(displayMode && getPreserveAspectRatioString(displayMode).preserveAspectRatioString) || 'none'
	const attributes = {
		ref: 'image', // keep this - we replace it with image placeholder inside the component
		key: 'image',
		'data-type': 'image',
		width: '100%',
		height: '100%',
		preserveAspectRatio,
	}
	const mask = maskId && `url(#${maskId})`
	const dataSvgMask = maskId && { 'data-svg-mask': `${maskId}-svg` }
	const filter = filterId && `url(#${filterId})`
	return pickBy(Object.assign(attributes, { mask, filter }, dataSvgMask), (x: any) => x)
}
// TODO move this to a common place (duplicated)
function stringifyAttrs(attrs: Record<string, string>) {
	return Object.keys(attrs)
		.map((k) => `${k}="${attrs[k]}"`)
		.join(' ')
}

const createElement = (tag: string, attrs: Record<string, string>, children: string): string => {
	const attrsString = stringifyAttrs(attrs)
	return children ? `<${tag} ${attrsString}>${children}</${tag}>` : `<${tag} ${attrsString} />`
}

const getMask = (maskId: string, svgId: string, svg: string, attributes: MaskDataAttrs) => {
	const width = attributes.width
	const height = attributes.height
	const x = attributes.x
	const y = attributes.y
	const transform = attributes.transform
	const style = attributes.style
	const transformStr = transform ? ` transform="${transform}"` : ''
	const styleStr = style ? ` style="${style}"` : ''
	const filterId = `${svgId}-bleach`
	const bleach = `<filter id="${filterId}"><feColorMatrix type="matrix"
			values="0 0 0 0 1
				0 0 0 0 1
				0 0 0 0 1
				0 0 0 1 0"/></filter>`
	const filterUrl = `filter="url(#${filterId})"`
	return `${bleach}
			<mask id="${maskId}">
				<use ${filterUrl}
				id="${svgId}-use" xlink:href="#${svgId}" width="${width}" height="${height}" x="${x}" y="${y}" ${transformStr} ${styleStr}></use>
			</mask>
			${svg}`
}

const createReactSvgImageWithMask = (
	compId: string,
	imageDimensions: ImageDimensions,
	filterId: string,
	maskId: string,
	maskData: MaskData,
	altText: string,
	displayMode: string
) => {
	const imageAttributes = getSvgImageAttributes(filterId, maskId, displayMode)

	const svgImageAttributes = getSvgAttributes(
		{
			role: 'img',
			'aria-label': altText,
		},
		compId
	)

	const svgDomId = `${maskId}-svg`
	const svgStringWithId = replaceSVGMaskAttributes(maskData.svgString, svgDomId)
	const maskAttrs = {
		width: '100%',
		height: '100%',
		x: 0,
		y: 0,
		...getMaskFlipTransformAttr(maskData.flip, imageDimensions),
	}

	const defsString = `<defs>${getMask(maskId, svgDomId, svgStringWithId, maskAttrs)}</defs>`
	const imageString = createElement('image', { ...imageAttributes }, '')
	return createElement('svg', svgImageAttributes, `${defsString}${imageString}`)
}

const getFontFamilyFontsFromSvg = (svgString: string): Array<Array<string>> => {
	const regex = /font-family=['"](.*?)['"]/gi
	let match
	const fonts: Array<Array<string>> = []

	do {
		match = regex.exec(svgString)
		if (match && match[1]) {
			const font = match[1].split(',').map((f) => f.replace(/'/g, '').trim())
			fonts.push(font)
		}
	} while (match)
	return fonts
}

const buildSvgUrlFactory = () => {
	const addTrailingSlashIfMissing = (val: string) => (val.endsWith('/') ? val : `${val}/`)
	const getSvgBaseUrl = (mediaRootUrl: string) => `${addTrailingSlashIfMissing(mediaRootUrl)}shapes/`
	const svgIdToUri = (svgId: string) => {
		const partsArr = svgId.replace(/^.*\//, '').split('.')
		const version = partsArr[1] === 'v1' ? 1 : 2
		const svgHash = partsArr[2].replace(/svg_/i, '')
		const svgName = partsArr[3]
		return `${svgHash + (version === 1 ? `_svgshape.v1.${svgName}` : '')}.svg`
	}

	return {
		getSvgBaseUrl,
		buildSvgUrl: (mediaRootUrl: string, svgId: string) => {
			if (/^svgshape\.v[12]/.test(svgId)) {
				const svgUri = svgIdToUri(svgId)
				return `${getSvgBaseUrl(mediaRootUrl)}${svgUri}`
			}
			return `${getSvgBaseUrl(mediaRootUrl)}${svgId}`
		},
	}
}

export {
	getFontFamilyFontsFromSvg,
	transformVectorImage,
	parseSvgString,
	removeWidthAndHeight,
	createReactSvgImageWithMask,
	getFilter,
	getScaledSvgViewBox,
	replaceSVGMaskAttributes,
	getSvgContentForMask,
	encodeSVG,
	getCSSDropShadow,
	getCSSStrokeAdjustments,
	buildSvgUrlFactory,
}
